import {Navigate, useRoutes} from 'react-router-dom';
// layouts
import LandingLayout from 'src/layouts/landing';
import AppLayout from 'src/layouts/app';
// config
// import { PATH_AFTER_LOGIN } from 'src/config-global';
//
import {lazy, Suspense, useEffect, useState} from "react";
import {Socket} from "socket.io-client";
import { Stack } from '@mui/system';
import {paths} from "../paths";
import {authRoutes} from "./auth";
import {AuthGuard} from "../../auth/guard";
import {useAuthContext} from "../../auth/hooks";
import {AdminGuard} from "../../auth/guard/auth-guard";
import {useRouter} from "../hooks";
import {SplashScreen} from "../../components/loading-screen";
import {disconnectSocket, getSocket} from "../../api/socket";
import { MatematicoGuard} from "../../auth/guard/server-guard";
import {getServerName} from "../../utils/serverName";
// import { authRoutes } from './auth';
// import { authDemoRoutes } from './auth-demo';
// import { dashboardRoutes } from './dashboard';
// import { componentsRoutes } from './components';

// ----------------------------------------------------------------------

export const ProblemsListPage = lazy(() => import('src/pages/problems/list'));
export const ProblemsListForOwnerPage = lazy(() => import('src/pages/problems/listOwner'));
export const ProblemDetailsPage = lazy(() => import('src/pages/problems/details'));
export const PrivacyPage = lazy(() => import('src/pages/privacy'));
export const ProblemDetailsSiblingPage = lazy(() => import('src/pages/problems/detailsSibling'));
export const PricingPage = lazy(() => import('src/pages/pricing'));
export const LeaderboardPage = lazy(() => import('src/pages/leaderboard'));
export const UserProfilePage = lazy(() => import('src/pages/profile'));
export const TermsPage = lazy(() => import('src/pages/terms'));
export const LuchoPage = lazy(() => import('src/pages/lucho'));
export const TestsLandingPage = lazy(() => import('src/pages/tests'));
export const Test = lazy(() => import('src/pages/test'));
export const GuestPage = lazy(() => import('src/pages/auth/jwt/guest'));
export const AdminChatPage = lazy(() => import('src/pages/admin/adminchat'));
export const NormalUserChatPage = lazy(() => import('src/pages/normaluserchat'));
export const ProblemFightsPage = lazy(() => import('src/pages/problems/fights'));
export const UpdateRequiredPage = lazy(() => import('src/pages/auth/jwt/update'));
export const NVOListPage = lazy(() => import('src/pages/nvolist'));
export const NVOSolvePage = lazy(() => import('src/pages/nvosolve'));
export const NVOMathSolvePage = lazy(() => import('src/pages/nvomathsolve'));
export const NVOLangSolvePage = lazy(() => import('src/pages/nvolangsolve'));
export const NVOEditPage = lazy(() => import('src/pages/nvoedit'));
export const NVOSignUpPage = lazy(() => import('src/pages/nvosignup'));

export const ComingSoon = lazy(() => import('src/pages/coming-soon'));
export const Page404 = lazy(() => import('src/pages/404'));
export const AdminEditProblemPage = lazy(() => import('src/pages/admin/admineditproblem'));
export const AdminToolsPage = lazy(() => import('src/pages/admin/tools'));
export const NvoSchoolResultsPage = lazy(() => import('src/pages/admin/nvoschoolresults'));
export const NvoSchoolResultsEnterPage = lazy(() => import('src/pages/admin/nvoschoolresultsenter'));


export default function Router() {
  const {authenticated, user, reloadNotifications, updateStateNVO, reloadNvo} = useAuthContext();
  const router = useRouter();
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (user) {
      setSocket(getSocket(user));
    } else {
      setSocket(disconnectSocket());
    }
  }, [user]);

  useEffect(() => {
    if (typeof reloadNotifications !== 'function') return () => {
    };
    const intervalId = setInterval(() => {
      reloadNotifications().catch(console.error);
    }, 300000);

    return () => clearInterval(intervalId);

  }, [reloadNotifications]);

  useEffect(() => {
    if (typeof reloadNvo !== 'function') return () => {};
    if (user?.state?.nvo) {
      return () => {};
    }
    const intervalId = setInterval(() => {
      if (user?.state?.nvo) {
        return;
      }
      reloadNvo().catch(console.error);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [reloadNvo, user?.state, user?.state?.nvo]);

  useEffect(() => {
    if (user?.state?.nvo) {
      if (user.state.nvo.subject === 'math') {
        router.push(paths.nvo.nvoMathSolve);
      } else {
        router.push(paths.nvo.nvoLangSolve);
      }
    }
  }, [router, user?.state?.nvo]);

  useEffect(() => {
    socket?.on('stateNVOUpdated', (data: any) => {
      updateStateNVO(data);
    });
    return () => {
      socket?.off('stateNVOUpdated');
    }
  }, [socket, updateStateNVO]);

  return useRoutes([
    {
      path: '/',
      element: (
        // eslint-disable-next-line no-nested-ternary
        authenticated ?
          ((getServerName() === 'Matematico') ? <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              {user?.isGroupOwner && <ProblemsListForOwnerPage/>}
              {user?.isGroupOwner || <ProblemsListPage/>}
            </Suspense>
          </AppLayout> : <AppLayout>
            <Suspense fallback={<SplashScreen />}>
              <Stack
                sx={{
                  py: 12,
                  m: 'auto',
                  maxWidth: 400,
                  minHeight: '100vh',
                  textAlign: 'center',
                  justifyContent: 'center',
                }}
              >
                <ComingSoon  />
              </Stack>
            </Suspense>
          </AppLayout>) :
          <Navigate to={paths.auth.jwt.login} replace/>
      ),
    },
    {
      path: '/update',
      element: (
        <Suspense fallback={<SplashScreen/>}>
          <UpdateRequiredPage/>
        </Suspense>
      ),
    },
    {
      path: '/problem',
      element: (
        <MatematicoGuard>
          <AuthGuard>
            <AppLayout>
              <Suspense fallback={<SplashScreen/>}>
                {user?.isGroupOwner && <ProblemsListForOwnerPage/>}
                {user?.isGroupOwner || <ProblemsListPage/>}
              </Suspense>
            </AppLayout>
          </AuthGuard>
        </MatematicoGuard>
      ),
    },
    {
      path: '/test',
      element: (
        <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <Test/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
        </MatematicoGuard>
      ),
    },
    {
      path: paths.nvo.list,
      element: (
      <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <NVOListPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      </MatematicoGuard>
      ),
    },
    {
      path: paths.nvo.solve,
      element: (
    <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <NVOSolvePage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: paths.nvo.nvoMathSolve,
      element: (
        <MatematicoGuard>
          <AuthGuard>
            <AppLayout>
              <Suspense fallback={<SplashScreen/>}>
                <NVOMathSolvePage/>
              </Suspense>
            </AppLayout>
          </AuthGuard>
        </MatematicoGuard>
      ),
    },
    {
      path: paths.nvo.nvoLangSolve,
      element: (
        <MatematicoGuard>
          <AuthGuard>
            <AppLayout>
              <Suspense fallback={<SplashScreen/>}>
                <NVOLangSolvePage/>
              </Suspense>
            </AppLayout>
          </AuthGuard>
        </MatematicoGuard>
      ),
    },
    {
      path: '/nvo/edit/:id',
      element: (
      <MatematicoGuard>
        <AdminGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <NVOEditPage/>
            </Suspense>
          </AppLayout>
        </AdminGuard>
      </MatematicoGuard>
      ),
    },
    {
      path: paths.nvo.signUp,
      element: (
        <MatematicoGuard>
          <AuthGuard>
            <AppLayout>
              <Suspense fallback={<SplashScreen/>}>
                <NVOSignUpPage/>
              </Suspense>
            </AppLayout>
          </AuthGuard>
        </MatematicoGuard>
      ),
    },
    {
      path: '/problem/:id',
      element: (
    <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <ProblemDetailsPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: '/problem/:id/test',
      element: (
      <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <ProblemDetailsSiblingPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      </MatematicoGuard>
      ),
    },
    {
      path: '/problem/:id/test',
      element: (
    <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <ProblemDetailsSiblingPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: paths.page404,
      element: (
        <AuthGuard>
          <LandingLayout>
            <Suspense fallback={<SplashScreen/>}>
              <Page404/>
            </Suspense>
          </LandingLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.pricing,
      element: (
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <PricingPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.leaderboard,
      element: (
    <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <LeaderboardPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: paths.user.root,
      element: (
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <UserProfilePage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/user/:id',
      element: (
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <UserProfilePage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/terms',
      element: (
        <LandingLayout>
          <Suspense fallback={<SplashScreen/>}>
            <TermsPage/>
          </Suspense>
        </LandingLayout>
      ),
    },
    {
      path: '/privacy',
      element: (
        <LandingLayout>
          <Suspense fallback={<SplashScreen/>}>
            <PrivacyPage/>
          </Suspense>
        </LandingLayout>
      ),
    },
    {
      path: '/lucho',
      element: (
        <MatematicoGuard>
          <LandingLayout>
            <Suspense fallback={<SplashScreen/>}>
              <LuchoPage/>
            </Suspense>
          </LandingLayout>
        </MatematicoGuard>
      ),
    },
    {
      path: '/test/generate',
      element: (
    <MatematicoGuard>
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <TestsLandingPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: '/adminchat',
      element: (
        <AdminGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <AdminChatPage/>
            </Suspense>
          </AppLayout>
        </AdminGuard>
      ),
    },
    {
      path: '/admin/editproblem/:id',
      element: (
    <MatematicoGuard>
        <AppLayout>
          <Suspense fallback={<SplashScreen/>}>
            <AdminEditProblemPage/>
          </Suspense>
        </AppLayout>
    </MatematicoGuard>
      ),
    },
    {
      path: '/contacts/chat',
      element: (
        <AuthGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <NormalUserChatPage/>
            </Suspense>
          </AppLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/problemfights',
      element: (
    <MatematicoGuard>
        <AdminGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <ProblemFightsPage/>
            </Suspense>
          </AppLayout>
        </AdminGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: paths.admin.nvoSchoolResults,
      element: (
      <MatematicoGuard>
        <AdminGuard>
            <Suspense fallback={<SplashScreen/>}>
              <NvoSchoolResultsPage/>
            </Suspense>
        </AdminGuard>
      </MatematicoGuard>
      ),
    },
    {
      path: paths.admin.nvoSchoolResultsEnter,
      element: (
    <MatematicoGuard>
        <AdminGuard>
          <Suspense fallback={<SplashScreen/>}>
            <NvoSchoolResultsEnterPage/>
          </Suspense>
        </AdminGuard>
    </MatematicoGuard>
      ),
    },
    {
      path: paths.admin.tools,
      element: (
      <MatematicoGuard>
        <AdminGuard>
          <AppLayout>
            <Suspense fallback={<SplashScreen/>}>
              <AdminToolsPage/>
            </Suspense>
          </AppLayout>
        </AdminGuard>
      </MatematicoGuard>
      ),
    },
    ...authRoutes,
    {path: '*', element: <Navigate to="/404" replace/>},
  ]);
}
